import React from "react";
import { CircularProgress } from "@mui/material";
import { REJECTION_REASON_OPTIONS, validateRejectionReason } from "../../rejectionReasons";

const CancellationReview = ({
  selectedItems,
  eligibleItems,
  context,
  contextError,
  isLoadingContext,
  isCancelling,
  reason,
  customReason,
  error,
  markWastage,
  doNotCharge,
  onClose,
  onBack,
  onConfirm,
  onRetry,
  onReasonChange,
  onCustomReasonChange,
  onWastageChange,
  onDoNotChargeChange,
}) => (
  <div className="cancellation-review">
    <div className="cancellation-review-header">
      <div>
        <p className="cancellation-eyebrow">KITCHEN ORDER</p>
        <h2 className="edit-title" id="edit-order-title">Review cancellation</h2>
        <p className="cancellation-subtitle">
          Confirm what should happen to the selected {selectedItems.length === 1 ? "item" : "items"}.
        </p>
      </div>
      <button
        type="button"
        className="close-btn cancellation-close"
        aria-label="Close cancellation review"
        onClick={onClose}
        disabled={isCancelling}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 6 6 18"></path>
          <path d="m6 6 12 12"></path>
        </svg>
      </button>
    </div>
    <div className="cancellation-review-body">
      <div className="cancellation-scope">
        <div className="cancellation-scope-heading">
          <span>{selectedItems.length === eligibleItems.length ? "Whole order" : "Selected items"}</span>
          <strong>{selectedItems.length}</strong>
        </div>
        <ul className="cancellation-item-list">
          {selectedItems.map((item, index) => (
            <li key={item?.taskId || `${item?.id || "item"}-${index}`}>
              <span>{item?.name || item?.title || "Order item"}</span>
              <strong>× {item?.qty}</strong>
            </li>
          ))}
        </ul>
      </div>

      {isLoadingContext ? (
        <div className="cancellation-context-state" role="status">
          <CircularProgress size={20} />
          <span>Checking linked cart and payment status…</span>
        </div>
      ) : contextError ? (
        <div className="cancellation-context-error" role="alert">
          <p>{contextError}</p>
          <button
            type="button"
            className="cancellation-secondary-btn"
            onClick={onRetry}
            disabled={isCancelling}
          >
            Retry cart check
          </button>
        </div>
      ) : context ? (
        <>
          <fieldset className="cancellation-fieldset">
            <legend>Cancellation reason <span aria-hidden="true">*</span></legend>
            <label className="sr-only" htmlFor="cancellation-reason">Cancellation reason</label>
            <select
              id="cancellation-reason"
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              disabled={isCancelling}
            >
              <option value="">Choose a reason</option>
              {REJECTION_REASON_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {reason === "OTHER" && (
              <>
                <label className="sr-only" htmlFor="custom-cancellation-reason">
                  Custom cancellation reason
                </label>
                <textarea
                  id="custom-cancellation-reason"
                  aria-label="Custom cancellation reason"
                  value={customReason}
                  onChange={(event) => onCustomReasonChange(event.target.value)}
                  maxLength={500}
                  placeholder="Describe the cancellation"
                  rows={3}
                  disabled={isCancelling}
                />
              </>
            )}
          </fieldset>

          <fieldset className="cancellation-fieldset">
            <legend>Wastage handling <span aria-hidden="true">*</span></legend>
            {context.cartId ? (
              <div className="cancellation-choice-list">
                <label>
                  <input
                    type="radio"
                    name="cancellation-wastage"
                    checked={markWastage === true}
                    onChange={() => onWastageChange(true)}
                    disabled={isCancelling}
                  />
                  <span>
                    <strong>Mark as wastage</strong>
                    <small>Record the cancelled quantity as wastage.</small>
                  </span>
                </label>
                <label>
                  <input
                    type="radio"
                    name="cancellation-wastage"
                    checked={markWastage === false}
                    onChange={() => onWastageChange(false)}
                    disabled={isCancelling}
                  />
                  <span>
                    <strong>Do not mark as wastage</strong>
                    <small>Return the quantity without recording wastage.</small>
                  </span>
                </label>
              </div>
            ) : (
              <p className="cancellation-note">
                This order has no linked cart. Wastage is unavailable; whole-order cancellation will use the existing kitchen order flow without a billing or wastage change.
              </p>
            )}
          </fieldset>

          <div className="cancellation-billing">
            <label className={context.unpaid ? "" : "is-disabled"}>
              <input
                type="checkbox"
                checked={doNotCharge}
                onChange={(event) => onDoNotChargeChange(event.target.checked)}
                disabled={!context.unpaid || isCancelling}
              />
              <span><strong>Do not charge in the bill</strong></span>
            </label>
            <p>
              {context.unpaid
                ? "Available because the linked cart is confirmed unpaid."
                : context.paid
                ? "The cart is paid. Billing changes and refunds are not available from this drawer."
                : context.cartId
                ? "Payment status is unconfirmed. Billing changes are disabled."
                : "No linked cart is available for billing changes."}
            </p>
            <button type="button" className="cancellation-refund-option" disabled>
              Process refund
            </button>
            <p className="cancellation-note">
              Refund processing is not wired to a verified payment integration. No refund will be issued here, including for paid or QSR orders.
            </p>
          </div>
        </>
      ) : null}

      {error && <p className="cancellation-form-error" role="alert">{error}</p>}
    </div>
    <div className="cancellation-review-footer">
      <button
        type="button"
        className="cancellation-secondary-btn"
        onClick={onBack}
        disabled={isCancelling}
      >
        Back
      </button>
      <button
        type="button"
        className="cancellation-confirm-btn"
        onClick={onConfirm}
        disabled={isCancelling || isLoadingContext || !context || !!contextError ||
          selectedItems.length === 0 || markWastage === null ||
          Boolean(validateRejectionReason(reason, customReason).error)}
      >
        {isCancelling ? (
          <><CircularProgress size={17} color="inherit" /> Cancelling…</>
        ) : selectedItems.length === eligibleItems.length ? (
          "Cancel order"
        ) : selectedItems.length === 1 ? (
          "Cancel item"
        ) : (
          "Cancel items"
        )}
      </button>
    </div>
  </div>
);

export default CancellationReview;
